'use client';

import { useState } from 'react';
import {
  trackLoanCalculatorCalculate,
  trackLoanCalculatorReset,
  trackLoanCalculatorTryExtraPayment,
} from 'components/analytics/eventHandlerTracking';
import { CalculatorShell } from 'components/Widget/shared/CalculatorShell';
import { useResultScroll } from 'components/Widget/shared/useResultScroll';
import { formatAmount } from 'components/Widget/shared/currency';
import { deferTracking } from 'components/Widget/shared/deferTracking';
import { useCountFields } from 'components/Widget/shared/useCountFields';
import { useCurrencyFields } from 'components/Widget/shared/useCurrencyFields';
import { usePercentFields } from 'components/Widget/shared/usePercentFields';
import { valueOrPlaceholder } from 'components/Widget/shared/types';
import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import { calculateLoan } from './loan';
import { LoanCalculatorForm } from './LoanCalculatorForm';
import { LoanCalculatorResult } from './LoanCalculatorResult';
import { LoanCalculatorSummary } from './LoanCalculatorSummary';
import {
  DEFAULT_RATE_TYPE,
  DEFAULT_TERM_UNIT,
  DISCLAIMER,
  EXTRA_PAYMENT_FIELD,
  getRateType,
  getTermUnit,
  INITIAL_CURRENCY_VALUES,
  INITIAL_RATE_VALUES,
  INITIAL_TERM_VALUES,
  LOAN_AMOUNT_FIELD,
  LOAN_TERM_FIELD,
  RATE_FIELD,
  TITLE,
} from './constants';
import { validateLoanForm } from './validation';
import type { LoanCalculatorState, RateType, TermUnit } from './types';

const INITIAL_STATE: LoanCalculatorState = { errors: {}, panel: null };

/** Substitutes each untouched field's greyed default, so Calculate reads the numbers on screen. */
const withPlaceholders = (
  values: CalculatorFieldValues,
  fields: readonly CalculatorField[],
  isTouched: (label: string) => boolean,
): CalculatorFieldValues => {
  const resolved = { ...values };

  fields.forEach(field => {
    resolved[field.label] = valueOrPlaceholder(values[field.label], field, isTouched(field.label));
  });

  return resolved;
};

export const LoanCalculator = () => {
  const [state, setState] = useState<LoanCalculatorState>(INITIAL_STATE);
  const [rateType, setRateType] = useState<RateType>(DEFAULT_RATE_TYPE);
  const [termUnit, setTermUnit] = useState<TermUnit>(DEFAULT_TERM_UNIT);
  const [isExtraPaymentOn, setIsExtraPaymentOn] = useState(false);
  // Separate from the panel so the comparison opens and closes without recalculating anything.
  const [showComparison, setShowComparison] = useState(false);
  // The amount and term fields already show their own affix, so blur must not format a second `$` in.
  const currency = useCurrencyFields(INITIAL_CURRENCY_VALUES, formatAmount);
  const rate = usePercentFields(INITIAL_RATE_VALUES);
  const term = useCountFields(INITIAL_TERM_VALUES);
  const { resultsRef, scrollToResults } = useResultScroll();

  const handleCalculate = () => {
    // An untouched field calculates on the greyed default the user can see, rather than failing validation.
    const currencyValues = withPlaceholders(currency.normalize(), [LOAN_AMOUNT_FIELD], currency.isTouched);
    const rateValues = withPlaceholders(rate.normalize(), [RATE_FIELD], rate.isTouched);
    const termValues = withPlaceholders(term.normalize(), [LOAN_TERM_FIELD], term.isTouched);
    const errors = validateLoanForm({
      loanAmount: currencyValues[LOAN_AMOUNT_FIELD.label],
      rate: rateValues[RATE_FIELD.label],
      term: termValues[LOAN_TERM_FIELD.label],
      termUnit,
      isExtraPaymentOn,
      extraPayment: currencyValues[EXTRA_PAYMENT_FIELD.label],
    });

    // The form stays mounted beside the results, so a field still being edited keeps its unformatted digits.
    currency.commit();
    rate.commit();
    term.commit();

    // The press is what is counted, so an attempt validation turned back still lands in the funnel.
    deferTracking(() => {
      trackLoanCalculatorCalculate({
        label: getRateType(rateType).label,
        item_value: `${getTermUnit(termUnit).label}, ${isExtraPaymentOn ? 'with extra payment' : 'no extra payment'}`,
      });
    });

    if (Object.keys(errors).length > 0) {
      // A failed Calculate raises the field errors and leaves any result already on screen alone.
      setState(previous => ({ ...previous, errors }));

      return;
    }

    const result = calculateLoan({
      loanAmount: currencyValues[LOAN_AMOUNT_FIELD.label],
      rateType,
      rate: rateValues[RATE_FIELD.label],
      term: termValues[LOAN_TERM_FIELD.label],
      termUnit,
      // An empty string is what makes `calculateLoan` model the teaser rather than a figure the user owns.
      extraPayment: isExtraPaymentOn ? currencyValues[EXTRA_PAYMENT_FIELD.label] : '',
    });
    setState({ errors: {}, panel: { result } });
    scrollToResults();
    // A typed amount opens straight into the comparison; the teaser's 5% stays behind the toggle.
    setShowComparison(result.isExtraPaymentFromUser);
  };

  const handleReset = () => {
    setState(INITIAL_STATE);
    setRateType(DEFAULT_RATE_TYPE);
    setTermUnit(DEFAULT_TERM_UNIT);
    setIsExtraPaymentOn(false);
    setShowComparison(false);
    currency.reset();
    rate.reset();
    term.reset();

    deferTracking(() => {
      trackLoanCalculatorReset();
    });
  };

  const handleToggleComparison = (value: boolean) => {
    setShowComparison(value);

    if (!value) return;

    deferTracking(() => {
      trackLoanCalculatorTryExtraPayment({
        label: state.panel?.result.isExtraPaymentFromUser ? 'Entered' : 'Modelled',
      });
    });
  };

  return (
    <CalculatorShell
      title={TITLE}
      disclaimer={DISCLAIMER}
      collapsibleDisclaimer
      summary={state.panel && <LoanCalculatorSummary panel={state.panel} />}
      resultsRef={resultsRef}
      // `null` rather than nothing: it reserves the 4fr column, so the form keeps its 60% and the
      // layout does not shift when the first calculation renders a panel here.
      results={
        state.panel && (
          <LoanCalculatorResult
            panel={state.panel}
            showComparison={showComparison}
            onToggleComparison={handleToggleComparison}
          />
        )
      }>
      <LoanCalculatorForm
        errors={state.errors}
        currencyFieldProps={currency.fieldProps}
        rateFieldProps={rate.fieldProps}
        termFieldProps={term.fieldProps}
        rateType={rateType}
        termUnit={termUnit}
        isExtraPaymentOn={isExtraPaymentOn}
        onRateTypeChange={setRateType}
        onTermUnitChange={setTermUnit}
        onExtraPaymentToggle={setIsExtraPaymentOn}
        onCalculate={handleCalculate}
        onReset={handleReset}
      />
    </CalculatorShell>
  );
};
